import React, { useEffect, useRef, useState } from 'react';
import mapboxgl, { MapMouseEvent } from 'mapbox-gl';

import 'mapbox-gl/dist/mapbox-gl.css';
import { BuildingWithConvertedAddresses, LatLng } from '../utils/types';
import { getCookie } from '../utils/utils';
import './HouseMap.scss';
import { performReverseGeocodingAndFetchPOIs } from './BuildingMap.helpers';
import LoadingSpinner from '../common/LoadingSpinner';
import RightArrow from '../AppContent/RightArrow';
import TagManager from 'react-gtm-module';
import { Trans, useTranslation } from 'react-i18next';
interface MapBoxExampleProps {
  mapCenter: LatLng;
  selectedBuilding: BuildingWithConvertedAddresses | null;
  triggeredFromSearch: boolean;
  feedIsOpen: boolean;
  mobileFeedIsOpen: boolean;
  countryOfFeedContent: string;
  lastMapBoundaryBoxCenter: LatLng | null;
  lastZoomLevel: number | null;
  initialMapZoom: number;
  isLandingPage?: boolean;
  isSmallMap?: boolean;
  setShowFeed: (showFeed: boolean) => void;
  setTriggeredFromSearch: (triggeredFromSearch: boolean) => void;
  selectBuilding: (building: BuildingWithConvertedAddresses) => void;
  setCountryOfFeedContent: (country: string) => void;
  setLastMapBoundaryBoxCenter: (lastMapBoundaryBoxCenter: LatLng) => void;
  setLastZoomLevel: (lastZoomLevel: number) => void;
}

const BuildingMap = ({
  mapCenter,
  triggeredFromSearch,
  selectedBuilding,
  feedIsOpen,
  mobileFeedIsOpen,
  countryOfFeedContent,
  lastMapBoundaryBoxCenter,
  lastZoomLevel,
  isLandingPage,
  isSmallMap,
  setShowFeed,
  selectBuilding,
  setTriggeredFromSearch,
  setCountryOfFeedContent,
  setLastMapBoundaryBoxCenter,
  setLastZoomLevel,
  initialMapZoom,
}: MapBoxExampleProps) => {
  const { i18n } = useTranslation();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const speechBubbleRef = useRef<HTMLDivElement>(null);
  const jaakkoInfoRef = useRef<HTMLDivElement>(null);
  const styleUrlRef = useRef<string>('');

  const prevSelectedBuildingRef = useRef<BuildingWithConvertedAddresses | null>(
    null,
  );

  const [showZoomHint, setShowZoomHint] = useState<boolean>(() => {
    const storedValue = localStorage.getItem('showZoomHint');
    return storedValue === null ? true : storedValue === 'true';
  });

  const [showClickBuildingHint, setShowClickBuildingHint] = useState<boolean>(
    () => {
      const storedValue = localStorage.getItem('showClickBuildingHint');
      return storedValue === null ? true : storedValue === 'true';
    },
  );

  const [showClickJaakkoHint, setShowClickJaakkoHint] = useState<boolean>(
    () => {
      const storedValue = localStorage.getItem('showClickJaakkoHint');
      return storedValue === null ? true : storedValue === 'true';
    },
  );

  const [showJaakkoSpeechBubble, setShowJaakkoSpeechBubble] = useState(false);
  const [zoom, setZoom] = useState(15.5);

  const animationProgressRef = useRef(0);
  let animationFrameId = useRef<number>();

  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);
  const smallMapRecoveryNonceRef = useRef(0);

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVh();

    window.addEventListener('resize', setVh);

    return () => {
      window.removeEventListener('resize', setVh);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        speechBubbleRef.current &&
        !speechBubbleRef.current.contains(event.target as Node) &&
        jaakkoInfoRef.current &&
        !jaakkoInfoRef.current.contains(event.target as Node)
      ) {
        setShowJaakkoSpeechBubble(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [speechBubbleRef, jaakkoInfoRef]);

  // Initialize the map only once
  useEffect(() => {
    try {
      mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

      const initialMapCenter = lastMapBoundaryBoxCenter || mapCenter;
      styleUrlRef.current = `mapbox://styles/jaakkocom/${process.env.REACT_APP_MAPBOX_STYLE_ID}`;

      // Check WebGL support before initializing map
      if (!mapboxgl.supported()) {
        setMapError('WebGL is not supported by this browser. Please use a modern browser with WebGL support.');
        return;
      }

      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current!,
        style: styleUrlRef.current,
        center: [initialMapCenter.lng, initialMapCenter.lat],
        zoom: lastZoomLevel || initialMapZoom,
        // Add failIfMajorPerformanceCaveat to false to allow software rendering
        failIfMajorPerformanceCaveat: false,
        // Add preserveDrawingBuffer for better compatibility
        preserveDrawingBuffer: true,
      });
    } catch (error) {
      console.error('Failed to initialize map:', error);
      setMapError('Failed to initialize the map. This may be due to limited graphics capabilities. Please try refreshing or using a different browser.');
      return;
    }

    // Attach the 'load' event listener *after* initializing the map
    mapRef.current.on('load', () => {
      try {
        console.log('Map loaded');
        // Change cursor to pointer when hovering over a building
        mapRef.current!.on('mouseenter', 'building', () => {
          mapRef.current!.getCanvas().style.cursor = 'pointer';
        });

        mapRef.current!.on('mouseleave', 'building', () => {
          mapRef.current!.getCanvas().style.cursor = '';
        });

        mapRef.current!.on('mouseenter', 'test-new-building', () => {
          mapRef.current!.getCanvas().style.cursor = 'pointer';
        });

        mapRef.current!.on('mouseleave', 'test-new-building', () => {
          mapRef.current!.getCanvas().style.cursor = '';
        });

        mapRef.current!.on('mouseenter', 'contractor-icon-backgrounds', () => {
          mapRef.current!.getCanvas().style.cursor = 'pointer';
        });

        mapRef.current!.on('mouseleave', 'contractor-icon-backgrounds', () => {
          mapRef.current!.getCanvas().style.cursor = '';
        });

        mapRef.current!.on('click', (e: MapMouseEvent) =>
          handleOnClickBuildingShowBuildingPage(e, mapRef, selectBuilding),
        );

        // Start the animation when the layer is ready
        mapRef.current!.on('sourcedata', () => {
          if (
            mapRef.current?.getLayer('building-jaakko-outline') &&
            !animationFrameId.current
          ) {
            // Start the animation
            animateGlow();
          }
        });

        if (!isLandingPage) {
          mapRef.current!.addControl(new mapboxgl.NavigationControl(), 'top-right');
        }
        // Add a custom class to the navigation control
        document
          .querySelector('.mapboxgl-ctrl-top-right')
          ?.classList.add('custom-navigation-control');

        // --- Add Contractor Icon Layer ---
        // Skip heavy, optional layers on small landing page map for stability
        if (isSmallMap) {
          return;
        }
        const contractorIconUrl = "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS-JCKa_fZkZGEeaMIx5wi09V5qeUHyeoVgtA&s";

        mapRef.current!.loadImage(contractorIconUrl, (error, contractorImage) => {
          if (error) {
            console.error('Error loading contractor icon:', error);
            return;
          }
          if (!contractorImage) {
            console.error('Contractor image loaded successfully but is null/undefined.');
            return;
          }

          // 1. Add the actual contractor icon image
          if (!mapRef.current!.hasImage('contractor-icon')) {
            mapRef.current!.addImage('contractor-icon', contractorImage);
          }

          // 2. Generate and add the white circle background image
          if (!mapRef.current!.hasImage('white-circle-background')) {
            const size = 64; // Size of the circle background image
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.beginPath();
              ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2); // Draw arc slightly smaller to keep stroke inside bounds

              // Fill the circle
              ctx.fillStyle = 'white'; // Make the circle white
              ctx.fill();

              // Add the outline
              ctx.strokeStyle = '#F7CF3E'; // Set the stroke color
              ctx.lineWidth = 1; // Set the stroke width (adjust as needed)
              ctx.stroke(); // Draw the outline

              const circleImageData = ctx.getImageData(0, 0, size, size);
              mapRef.current!.addImage('white-circle-background', circleImageData);
            }
          }

          // 3. Add the background circle layer (renders first)
          if (!mapRef.current!.getLayer('contractor-icon-backgrounds')) {
            mapRef.current!.addLayer({
              id: 'contractor-icon-backgrounds',
              type: 'symbol',
              source: 'composite',
              'source-layer': 'test_new_building', // Use the exact source layer name
              filter: ['==', ['get', 'type'], 'CONTRACTOR'],
              layout: {
                'icon-image': 'white-circle-background', // Use the generated circle
                'icon-size': 1.6, // Adjust size as needed - make it slightly larger than the icon
                'icon-allow-overlap': true,
              },
            });
            console.log("Contractor icon background layer added.");
          } else {
            console.log("Contractor icon background layer already exists.");
          }

          // 4. Add the contractor icon layer (renders on top of the background)
          if (!mapRef.current!.getLayer('contractor-icons')) {
            mapRef.current!.addLayer({
              id: 'contractor-icons',
              type: 'symbol',
              source: 'composite',
              'source-layer': 'test_new_building', // Use the exact source layer name
              filter: ['==', ['get', 'type'], 'CONTRACTOR'],
              layout: {
                'icon-image': 'contractor-icon', // Use the actual contractor icon
                'icon-size': 0.18,// Adjust size as needed
                'icon-allow-overlap': true,
              },
              // Ensure this layer is rendered *after* the background
              // Optional: use the 'before' parameter to explicitly place it relative to other layers if needed
              // 'before': 'some-other-layer-id'
            });
            console.log("Contractor icon layer added.");
          } else {
            console.log("Contractor icon layer already exists.");
          }
        });
        // --- End Contractor Icon Layer ---

      } catch (error) {
        console.error('Error during map load:', error);
        setMapError('Map failed to load properly. Please try refreshing the page.');
      }
      // The 'load' listener function should NOT return anything here.
    }); // End of map.on('load', ...)

    // Setup ResizeObserver *outside* the 'load' listener but inside useEffect
    const mapInstance = mapRef.current; // Capture instance
    const mapContainer = mapContainerRef.current; // Capture container
    let resizeObserver: ResizeObserver | null = null;
    let handleContextLost: ((event: Event) => void) | null = null;
    let handleContextRestored: (() => void) | null = null;

    if (mapInstance && mapContainer) {
      resizeObserver = new ResizeObserver(() => {
        mapInstance.resize();
      });
      resizeObserver.observe(mapContainer);

      // Attempt to recover if WebGL context is lost (seen on some mobile devices)
      const canvas = mapInstance.getCanvas();
      handleContextLost = (event: Event) => {
        // Prevent default so the context can be restored
        if (typeof (event as any).preventDefault === 'function') {
          (event as any).preventDefault();
        }
        // Reload style and resize after a tick
        setTimeout(() => {
          if (!mapRef.current) return;
          try {
            // Use the original style URL to avoid circular JSON issues
            if (styleUrlRef.current) {
              mapRef.current.setStyle(styleUrlRef.current);
              const refetchOnce = () => {
                try {
                  fetchBuildingsWithinBounds(mapRef);
                } catch (err) {
                  // ignore
                } finally {
                  if (mapRef.current) {
                    mapRef.current.off('styledata', refetchOnce);
                  }
                }
              };
              mapRef.current.on('styledata', refetchOnce);
            }
            mapRef.current.resize();
          } catch (err) {
            console.error('Failed to recover WebGL context', err);
          }
        }, 0);
      };
      handleContextRestored = () => {
        try {
          mapInstance.resize();
        } catch (err) {
          // no-op
        }
      };
      canvas.addEventListener('webglcontextlost', handleContextLost as EventListener, false);
      canvas.addEventListener('webglcontextrestored', handleContextRestored as EventListener, false);
    }

    // Add error handler for the map
    if (mapRef.current) {
      mapRef.current.on('error', (e) => {
        console.error('Map error:', e);
        setMapError('An error occurred while loading the map. Please try refreshing the page.');
      });
    }

    // Cleanup function is returned directly by useEffect
    return () => {
      console.log('Cleaning up map effect');
      resizeObserver?.disconnect(); // Disconnect observer if it exists
      // 1) Cancel any ongoing animation request
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = undefined;
      }

      // 2) Remove the map instance
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      // Detach context listeners if they were added
      if (mapInstance) {
        try {
          const canvas = mapInstance.getCanvas();
          if (handleContextLost) {
            canvas.removeEventListener('webglcontextlost', handleContextLost as EventListener, false);
          }
          if (handleContextRestored) {
            canvas.removeEventListener('webglcontextrestored', handleContextRestored as EventListener, false);
          }
        } catch (err) {
          // ignore
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once

  setTimeout(() => {
    if (zoom === 15.5) {
      setZoom(18);
    }
  }, 3000);

  // Update the map center smoothly when mapCenter changes
  useEffect(() => {
    if (!mapRef.current) return;

    if (triggeredFromSearch) {
      mapRef.current.flyTo({
        center: [mapCenter.lng, mapCenter.lat],
        essential: true,
        zoom,
      });
      setTriggeredFromSearch(false);
    } else {
      mapRef.current.flyTo({
        center: [mapCenter.lng, mapCenter.lat],
        essential: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapCenter]);

  useEffect(() => {
    if (prevSelectedBuildingRef.current && !selectedBuilding) {
      // Fetch buildings when selectedBuilding is cleared
      fetchBuildingsWithinBounds(mapRef);

      // For small maps (like landing page property map), also refresh the map
      // to prevent corruption after building modal is closed
      if (isSmallMap && mapRef.current) {
        // Use setTimeout to ensure the DOM has updated before resizing
        setTimeout(() => {
          if (mapRef.current && mapContainerRef.current) {
            // Check if the container is visible and has dimensions
            const containerRect = mapContainerRef.current.getBoundingClientRect();
            if (containerRect.width > 0 && containerRect.height > 0) {
              // Resize the map to fit the container
              mapRef.current.resize();

              // Force a repaint to ensure the map renders correctly
              mapRef.current.triggerRepaint?.();

              // Also invalidate the size to ensure proper rendering
              if (typeof mapRef.current.getCanvas === 'function') {
                const canvas = mapRef.current.getCanvas();
                if (canvas) {
                  // Trigger a reflow by accessing offsetHeight
                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                  canvas.offsetHeight;
                }
              }

              // As a last-resort recovery for some mobile browsers, reload style once
              // to ensure layers re-initialize after modal transitions
              try {
                smallMapRecoveryNonceRef.current += 1;
                const nonceAtStart = smallMapRecoveryNonceRef.current;
                setTimeout(() => {
                  if (!mapRef.current) return;
                  // Ensure we still are in the same recovery cycle
                  if (nonceAtStart !== smallMapRecoveryNonceRef.current) return;
                  // Use the original style URL to avoid passing a circular style object
                  if (styleUrlRef.current) {
                    mapRef.current.setStyle(styleUrlRef.current);
                    // After style reload, re-fetch buildings in view once style is ready
                    const refetchOnce = () => {
                      try {
                        fetchBuildingsWithinBounds(mapRef);
                      } catch (err) {
                        // ignore
                      } finally {
                        if (mapRef.current) {
                          mapRef.current.off('styledata', refetchOnce);
                        }
                      }
                    };
                    mapRef.current.on('styledata', refetchOnce);
                    mapRef.current.resize();
                  }
                }, 50);
              } catch (err) {
                // ignore
              }
            }
          }
        }, 100);
      }
    }
    prevSelectedBuildingRef.current = selectedBuilding;
  }, [selectedBuilding, isSmallMap]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const doFetch = () => {
      try {
        fetchBuildingsWithinBounds(mapRef);
      } catch (err) {
        // ignore
      }
    };

    if (map.isStyleLoaded()) {
      doFetch();
    } else {
      map.once('load', doFetch);
    }

    // Add event listener for 'moveend' to fetch buildings when map view changes
    map.on('moveend', doFetch);

    return () => {
      try {
        map.off('moveend', doFetch);
        map.off('load', doFetch);
      } catch (err) {
        // ignore
      }
    };
  }, []);

  // Function to animate the glow effect
  const animateGlow = () => {
    if (!mapRef.current) {
      return;
    }

    if (!mapRef.current.getLayer('building-jaakko-outline')) {
      return;
    }

    // Increase the animation's progress
    animationProgressRef.current += 0.02;

    // Example lineWidth logic
    const lineWidth =
      1.4 + 1.4 * Math.abs(Math.sin(animationProgressRef.current));
    mapRef.current.setPaintProperty(
      'building-jaakko-outline',
      'line-width',
      lineWidth,
    );

    const colorIntensity =
      60 + 40 * Math.abs(Math.sin(animationProgressRef.current));
    const color = `hsl(47, 92%, ${colorIntensity}%)`;
    mapRef.current.setPaintProperty(
      'building-jaakko-outline',
      'line-color',
      color,
    );

    // Request next frame
    animationFrameId.current = requestAnimationFrame(animateGlow);
  };

  const hideZoomHint = () => {
    setShowZoomHint(false);
    localStorage.setItem('showZoomHint', 'false');
  };

  const hideClickBuildingHint = () => {
    setShowClickBuildingHint(false);
    localStorage.setItem('showClickBuildingHint', 'false');
  };

  const hideClickJaakkoHint = () => {
    setShowClickJaakkoHint(false);
    localStorage.setItem('showClickJaakkoHint', 'false');
  };

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.resize();
    }
  }, [feedIsOpen, mobileFeedIsOpen]);

  useEffect(() => {
    if (!mapRef.current) return;

    const updateCountryFromMapCenter = () => {
      if (!mapRef.current) return;

      // Get the center coordinates of the current map view
      const center = mapRef.current.getCenter();

      // Use Mapbox's Geocoding API to get the country
      const geocodingUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${center.lng},${center.lat}.json?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}&types=country`;
      fetch(geocodingUrl)
        .then(response => response.json())
        .then(data => {
          if (data.features && data.features.length > 0) {
            // Extract the country name from the first feature
            const countryName = data.features[0].text;

            // Update the countryOfFeedContent if it's different
            if (countryName && (countryName !== countryOfFeedContent)) {
              setCountryOfFeedContent(countryName);
            }
          }
        })
        .catch(error => {
          console.error('Error determining country from map center:', error);
        });
    };

    // Function to handle the end of map movements
    const handleMoveEnd = () => {
      setLastMapBoundaryBoxCenter({
        lat: mapRef.current?.getCenter().lat,
        lng: mapRef.current?.getCenter().lng,
      });
      const currentZoom = mapRef.current?.getZoom();
      setLastZoomLevel(currentZoom);
      if (currentZoom < 10.5) {
        updateCountryFromMapCenter();
      }
    };

    // Add event listener for when map movement ends
    mapRef.current.on('moveend', handleMoveEnd);

    // Clean up
    return () => {
      if (mapRef.current) {
        mapRef.current.off('moveend', handleMoveEnd);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryOfFeedContent]);

  return (
    <div
      className={`map-wrapper ${feedIsOpen ? 'map-wrapper--feed-open' : ''} ${isLandingPage ? 'map-wrapper--landing-page-map' : ''}`}
      key={mobileFeedIsOpen ? 'feed-open' : 'feed-closed'}
    >
      {mapError ? (
        <div className="map-error-fallback">
          <div className="map-error-fallback__content">
            <div className="map-error-fallback__icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div className="map-error-fallback__title">
              <Trans i18nKey="map.error.title">Map unavailable</Trans>
            </div>
            <div className="map-error-fallback__message">
              {mapError}
            </div>
            <button
              className="map-error-fallback__retry-button"
              onClick={() => {
                setMapError(null);
                window.location.reload();
              }}
            >
              <Trans i18nKey="map.error.retry">Refresh page</Trans>
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="map-hints">
            {showClickBuildingHint && (
              <div
                className="click-building-hint"
                onClick={hideClickBuildingHint}
              >
                <Trans i18nKey="hints.clickBuilding" />
                <img
                  src="/assets/close.svg"
                  alt="Close"
                  height="22px"
                />
              </div>
            )}
            {showZoomHint && (
              <div className="zoom-in-hint"
                onClick={hideZoomHint}
              >
                <Trans i18nKey="hints.zoomInToSeeHouseNumbers" />
                <img
                  src="/assets/close.svg"
                  alt="Close"
                  height="22px"
                />
              </div>
            )}
            {showClickJaakkoHint && (
              <div
                className="click-jaakko-hint"
                onClick={hideClickJaakkoHint}
              >
                <Trans i18nKey="hints.expressInterest" />
                <img
                  src="/assets/close.svg"
                  alt="Close"
                  height="22px"
                  onClick={hideClickJaakkoHint}
                />
              </div>
            )}
            {showJaakkoSpeechBubble && (
              <div className="jaakko-speech-bubble" ref={speechBubbleRef}>
                <iframe
                  title="vimeo-player"
                  src={i18n.language.startsWith('fi') ? 'https://player.vimeo.com/video/1068410842' : 'https://player.vimeo.com/video/1068410835'}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allowFullScreen
                  // When the iframe finishes loading, mark it loaded
                  onLoad={() => setIsIframeLoading(false)}
                  style={{ display: isIframeLoading ? 'none' : 'block' }}
                ></iframe>

                {/* Conditionally show your loading indicator */}
                {isIframeLoading && (
                  <div className="loading-indicator">
                    <LoadingSpinner />
                  </div>
                )}
              </div>
            )}
            <div
              className="jaakko-info"
              onClick={() => {
                setShowJaakkoSpeechBubble(!showJaakkoSpeechBubble);
                TagManager.dataLayer({
                  dataLayer: {
                    event: 'jaakko_info_clicked',
                    category: 'User Interaction',
                    action: 'Click',
                    label: 'Jaakko Info Clicked',
                  },
                });
              }}
              ref={jaakkoInfoRef}
            >
              <img
                src="/assets/info_jaakko.png"
                alt="Jaakko info"
                height="60px"
              />
            </div>
          </div>

          {!feedIsOpen && (
            <button
              className="map-feed-vertical-button"
              onClick={() => setShowFeed(true)}
            >
              <div className="map-feed-vertical-button__text">
                <Trans i18nKey="feed.button.showFeed">
                  Fiidi
                </Trans>
              </div>
              <div className="map-feed-vertical-button__icon">
                <RightArrow />
              </div>
            </button>
          )}

          <div
            ref={mapContainerRef}
            id="map-container"
            className="map-container"
          ></div>
        </>
      )}
    </div>
  );
};

const handleOnClickBuildingShowBuildingPage = (
  e: MapMouseEvent,
  mapRef: React.MutableRefObject<mapboxgl.Map | null>,
  selectBuilding: (building: BuildingWithConvertedAddresses) => void,
) => {
  // Build the list of layers to query dynamically to avoid errors when a layer is not present
  const layersToQuery: string[] = ['building', 'test-new-building'];
  try {
    if (mapRef.current && mapRef.current.getLayer('contractor-icon-backgrounds')) {
      layersToQuery.push('contractor-icon-backgrounds');
    }
  } catch (err) {
    // If getLayer throws for any reason, ignore and continue with the base layers
  }

  const buildingFeatures = mapRef.current!.queryRenderedFeatures(e.point, {
    layers: layersToQuery,
  });

  if (buildingFeatures.length > 0) {
    const buildingFeature = buildingFeatures[0];
    const isContractor = buildingFeature.properties.type === 'CONTRACTOR';
    const customContractorAddress = {
      country: buildingFeature.properties.customCountry,
      region: buildingFeature.properties.customRegion,
      city: buildingFeature.properties.customCity,
      postcode: buildingFeature.properties.customPostcode,
      street: buildingFeature.properties.customStreet,
      houseNumber: buildingFeature.properties.customHouseNumber,
      coordinates: buildingFeature.properties.customCoordinates ? JSON.parse(buildingFeature.properties.customCoordinates) : { lat: 0, lng: 0 },
    }
    const building: BuildingWithConvertedAddresses = {
      id: isContractor ? buildingFeature.properties.customId : buildingFeature.id.toString(),
      centroid: { lat: e.lngLat.lat, lng: e.lngLat.lng },
      addresses: isContractor ? [
        customContractorAddress
      ] : [],
      geometry: { type: 'Polygon', coordinates: [] },
      properties: {
        name: isContractor ? buildingFeature.properties.name : '',
        addresses: isContractor ? [customContractorAddress] : [],
        // These are the CONTRACTOR properties
        type: buildingFeature.properties.type,
        introText: buildingFeature.properties.introText,
        logoUrl: buildingFeature.properties.logoUrl,
        infoText: buildingFeature.properties.infoText,
        propertyCount: buildingFeature.properties.propertyCount,
        customPropertyPhotoUrl: buildingFeature.properties.customPropertyPhotoUrl,
        infoTextEn: buildingFeature.properties.infoTextEn,
        introTextEn: buildingFeature.properties.introTextEn,
      },
      pois: [],
    };

    // Get building outline coordinates
    if (buildingFeature.geometry.type === 'Polygon') {
      building.geometry.coordinates = buildingFeature.geometry.coordinates;
    }

    if (isContractor) {
      selectBuilding(building);
    } else {
      performReverseGeocodingAndFetchPOIs(building, selectBuilding, e.lngLat);
    }
  } else {
    console.log('No building found at this location');
  }
};

const fetchBuildingsWithinBounds = (
  mapRef: React.MutableRefObject<mapboxgl.Map | null>,
) => {
  const bounds = mapRef.current!.getBounds();
  const bbox = {
    sw: [bounds.getWest(), bounds.getSouth()], // Southwest corner
    ne: [bounds.getEast(), bounds.getNorth()], // Northeast corner
  };

  fetch(`${process.env.REACT_APP_API_URL}/buildings-in-bounds`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': getCookie('csrf_access_token'),
    },
    credentials: 'include',
    body: JSON.stringify(bbox),
  })
    .then((response) => response.json())
    .then((data) => {
      // Process data to add "isRecentlyCommented" property
      const now = new Date();
      const oneDayInMs = 24 * 60 * 60 * 1000;

      data.features.forEach((feature) => {
        const isContractor = feature.properties.type === 'CONTRACTOR';
        const lastCommented = !isContractor && feature.properties.lastCommented;
        if (lastCommented) {
          const lastCommentedDate = new Date(lastCommented);
          const timeDiff = now.getTime() - lastCommentedDate.getTime();
          feature.properties.isRecentlyCommented = timeDiff <= oneDayInMs;
        } else {
          feature.properties.isRecentlyCommented = false;
        }
      });

      if (mapRef.current!.getSource('clicked-buildings')) {
        // Update existing source data
        (
          mapRef.current!.getSource(
            'clicked-buildings',
          ) as mapboxgl.GeoJSONSource
        ).setData(data);

        // Ensure all dependent images and layers exist (robust against race conditions)
        ensureClickedBuildingLayers(mapRef);
      } else {
        // Add new source and layers
        mapRef.current!.addSource('clicked-buildings', {
          type: 'geojson',
          data: data,
        });
        // Load and add the off market sell icon image
        mapRef.current!.loadImage(
          `${process.env.PUBLIC_URL}/assets/offMarketSellSmall.png`,
          (error, image) => {
            if (error) {
              console.error('Error loading off market sell icon:', error);
              return;
            }
            if (!mapRef.current!.hasImage('off-market-sell-icon')) {
              mapRef.current!.addImage('off-market-sell-icon', image!);
            }

            // Add the off market sell icon layer on top of the outline layer
            if (!mapRef.current!.getLayer('building-off-market-sell-icon')) {
              mapRef.current!.addLayer({
                id: 'building-off-market-sell-icon',
                type: 'symbol',
                source: 'clicked-buildings',
                filter: ['all',
                  ['>', ['get', 'could_sell_count'], 0],
                  ['!=', ['get', 'type'], 'CONTRACTOR'],
                ],
                minzoom: 1,
                layout: {
                  'icon-image': 'off-market-sell-icon',
                  'icon-size': 0.9,
                  'icon-offset': [
                    'case',
                    [
                      'all',
                      ['==', ['get', 'admin_comment_flag'], true],
                      ['!=', ['get', 'no_feed_comment_flag'], true],
                    ], // If Jaakko comment exists and no_feed_comment_flag is not true
                    [36, 20], // Offset to the right
                    [20, 16], // Otherwise, offset to the left
                  ],
                  'icon-allow-overlap': true,
                },
              });
            }
          },
        );

        // Load and add the off market buy icon image
        mapRef.current!.loadImage(
          `${process.env.PUBLIC_URL}/assets/offMarketBuySmall.png`,
          (error, image) => {
            if (error) {
              console.error('Error loading off market buy icon:', error);
              return;
            }
            if (!mapRef.current!.hasImage('off-market-buy-icon')) {
              mapRef.current!.addImage('off-market-buy-icon', image!);
            }

            // Add the off market buy icon layer on top of the outline layer
            if (!mapRef.current!.getLayer('building-off-market-buy-icon')) {
              mapRef.current!.addLayer({
                id: 'building-off-market-buy-icon',
                type: 'symbol',
                source: 'clicked-buildings',
                filter: ['all',
                  ['>', ['get', 'could_buy_count'], 0],
                  ['!=', ['get', 'type'], 'CONTRACTOR'],
                ],
                minzoom: 1,
                layout: {
                  'icon-image': 'off-market-buy-icon',
                  'icon-size': 0.9,
                  // Set dynamic icon-offset based on the presence of a Jaakko comment
                  'icon-offset': [
                    'case',
                    [
                      'all',
                      ['==', ['get', 'admin_comment_flag'], true],
                      ['!=', ['get', 'no_feed_comment_flag'], true],
                    ], // If Jaakko comment exists and no_feed_comment_flag is not true
                    [-36, 20], // Offset to the right
                    [-20, 16], // Otherwise, offset to the left
                  ],
                  'icon-allow-overlap': true,
                },
              });
            }
          },
        );

        // After the 'clicked-buildings-outline' layer, add the heart icons and like counts
        mapRef.current!.loadImage(
          `${process.env.PUBLIC_URL}/assets/heart.png`,
          (error, image) => {
            if (error) {
              console.error('Error loading heart icon:', error);
              return;
            }
            // Add the image to the map's style
            if (!mapRef.current!.hasImage('heart-icon')) {
              mapRef.current!.addImage('heart-icon', image!);
            }

            // Add a symbol layer to display the heart icon and like count on buildings with like_count > 0
            if (!mapRef.current!.getLayer('building-like-icons')) {
              mapRef.current!.addLayer({
                id: 'building-like-icons',
                type: 'symbol',
                source: 'clicked-buildings',
                filter: [
                  'all',
                  ['>', ['get', 'like_count'], 0],
                  ['!=', ['get', 'type'], 'CONTRACTOR'],
                ],
                minzoom: 1,
                layout: {
                  'icon-image': 'heart-icon',
                  'icon-size': 0.6,
                  'icon-offset': [-15, -16.5],
                  'icon-allow-overlap': true,
                  'text-field': ['get', 'like_count'],
                  'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
                  'text-size': 12,
                  'text-offset': [-0.75, -1.4],
                  'text-anchor': 'top',
                  'text-allow-overlap': true,
                },
                paint: {
                  'text-color': '#FFF',
                },
              });
            }
          },
        );

        // After the 'clicked-buildings-outline' layer, add the comment icons and comment counts
        mapRef.current!.loadImage(
          `${process.env.PUBLIC_URL}/assets/chata.png`,
          (error, image) => {
            if (error) {
              console.error('Error loading comment icon:', error);
              return;
            }
            // Add the image to the map's style
            if (!mapRef.current!.hasImage('comment-icon')) {
              mapRef.current!.addImage('comment-icon', image!);
            }

            // Add a symbol layer to display the comment icon and comment count on buildings with commentsCount > 0
            if (!mapRef.current!.getLayer('building-comment-icons')) {
              mapRef.current!.addLayer({
                id: 'building-comment-icons',
                type: 'symbol',
                source: 'clicked-buildings',
                filter: [
                  'any',
                  [
                    'all',
                    ['>', ['get', 'commentsCount'], 0],
                    ['!=', ['get', 'admin_comment_flag'], true],
                    ['!=', ['get', 'type'], 'CONTRACTOR'],
                  ],
                  [
                    'all',
                    ['>', ['get', 'commentsCount'], 1],
                    ['==', ['get', 'admin_comment_flag'], true],
                    ['!=', ['get', 'type'], 'CONTRACTOR'],
                  ],
                ],
                minzoom: 1,
                layout: {
                  'icon-image': 'comment-icon',
                  'icon-size': 0.8,
                  'icon-offset': [15, -13.5],
                  'icon-allow-overlap': true,
                  'text-field': ['get', 'commentsCount'],
                  'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
                  'text-size': 12,
                  'text-offset': [1, -1.6],
                  'text-anchor': 'top',
                  'text-allow-overlap': true,
                },
                paint: {
                  'text-color': '#202125',
                },
              });
            }
          },
        );

        // Add the outline layer before adding the icon layer
        if (!mapRef.current!.getLayer('building-jaakko-outline')) {
          mapRef.current!.addLayer({
            id: 'building-jaakko-outline',
            type: 'line',
            source: 'clicked-buildings',
            filter: ['all',
              ['==', ['get', 'isRecentlyCommented'], true],
              ['!=', ['get', 'type'], 'CONTRACTOR'],
            ],
            minzoom: 1,
            paint: {
              'line-color': '#F7CF3E', // Gold color for the outline
              'line-width': 1.4, // Adjust the width as needed
              'line-opacity': 0.8,
            },
          });
        }

        // Load and add the Jaakko icon image
        mapRef.current!.loadImage(
          `${process.env.PUBLIC_URL}/assets/jaakko_head.png`,
          (error, image) => {
            if (error) {
              console.error('Error loading Jaakko icon:', error);
              return;
            }
            if (!mapRef.current!.hasImage('jaakko-icon')) {
              mapRef.current!.addImage('jaakko-icon', image!);
            }

            // Add the Jaakko icon layer on top of the outline layer
            if (!mapRef.current!.getLayer('building-jaakko-emojis')) {
              mapRef.current!.addLayer({
                id: 'building-jaakko-emojis',
                type: 'symbol',
                source: 'clicked-buildings',
                filter: [
                  'all',
                  ['==', ['get', 'admin_comment_flag'], true],
                  ['!=', ['get', 'no_feed_comment_flag'], true],
                  ['!=', ['get', 'type'], 'CONTRACTOR'],
                ],
                minzoom: 1,
                layout: {
                  'icon-image': 'jaakko-icon',
                  'icon-size': 0.4,
                  'icon-offset': [0, 40],
                  'icon-allow-overlap': true,
                },
              });
            }
          },
        );
      }
    })
    .catch((error) => {
      console.error('Error fetching buildings:', error);
    });
};

// Ensure that all images and layers tied to 'clicked-buildings' exist.
// This is defensive against timing issues where the style reloads or
// the initial fetch happens before images/layers are registered.
const ensureClickedBuildingLayers = (
  mapRef: React.MutableRefObject<mapboxgl.Map | null>,
) => {
  const map = mapRef.current;
  if (!map) return;
  const sourceExists = !!map.getSource('clicked-buildings');
  if (!sourceExists) return;

  // Ensure required images are available
  const imageSpecs: Array<{ id: string; url: string }> = [
    { id: 'off-market-sell-icon', url: `${process.env.PUBLIC_URL}/assets/offMarketSellSmall.png` },
    { id: 'off-market-buy-icon', url: `${process.env.PUBLIC_URL}/assets/offMarketBuySmall.png` },
    { id: 'heart-icon', url: `${process.env.PUBLIC_URL}/assets/heart.png` },
    { id: 'comment-icon', url: `${process.env.PUBLIC_URL}/assets/chata.png` },
    { id: 'jaakko-icon', url: `${process.env.PUBLIC_URL}/assets/jaakko_head.png` },
  ];

  imageSpecs.forEach(({ id, url }) => {
    if (!map.hasImage(id)) {
      try {
        map.loadImage(url, (err, image) => {
          if (!err && image && !map.hasImage(id)) {
            map.addImage(id, image);
          }
        });
      } catch (err) {
        // ignore
      }
    }
  });

  // Add missing layers in a safe order
  if (!map.getLayer('building-jaakko-outline')) {
    try {
      map.addLayer({
        id: 'building-jaakko-outline',
        type: 'line',
        source: 'clicked-buildings',
        filter: ['all', ['==', ['get', 'isRecentlyCommented'], true], ['!=', ['get', 'type'], 'CONTRACTOR']],
        minzoom: 1,
        paint: {
          'line-color': '#F7CF3E',
          'line-width': 1.4,
          'line-opacity': 0.8,
        },
      });
    } catch (err) {
      // ignore
    }
  }

  if (!map.getLayer('building-off-market-sell-icon')) {
    try {
      map.addLayer({
        id: 'building-off-market-sell-icon',
        type: 'symbol',
        source: 'clicked-buildings',
        filter: ['all', ['>', ['get', 'could_sell_count'], 0], ['!=', ['get', 'type'], 'CONTRACTOR']],
        minzoom: 1,
        layout: {
          'icon-image': 'off-market-sell-icon',
          'icon-size': 0.9,
          'icon-offset': [
            'case',
            ['all', ['==', ['get', 'admin_comment_flag'], true], ['!=', ['get', 'no_feed_comment_flag'], true]],
            [36, 20],
            [20, 16],
          ],
          'icon-allow-overlap': true,
        },
      });
    } catch (err) {
      // ignore
    }
  }

  if (!map.getLayer('building-off-market-buy-icon')) {
    try {
      map.addLayer({
        id: 'building-off-market-buy-icon',
        type: 'symbol',
        source: 'clicked-buildings',
        filter: ['all', ['>', ['get', 'could_buy_count'], 0], ['!=', ['get', 'type'], 'CONTRACTOR']],
        minzoom: 1,
        layout: {
          'icon-image': 'off-market-buy-icon',
          'icon-size': 0.9,
          'icon-offset': [
            'case',
            ['all', ['==', ['get', 'admin_comment_flag'], true], ['!=', ['get', 'no_feed_comment_flag'], true]],
            [-36, 20],
            [-20, 16],
          ],
          'icon-allow-overlap': true,
        },
      });
    } catch (err) {
      // ignore
    }
  }

  if (!map.getLayer('building-like-icons')) {
    try {
      map.addLayer({
        id: 'building-like-icons',
        type: 'symbol',
        source: 'clicked-buildings',
        filter: ['all', ['>', ['get', 'like_count'], 0], ['!=', ['get', 'type'], 'CONTRACTOR']],
        minzoom: 1,
        layout: {
          'icon-image': 'heart-icon',
          'icon-size': 0.6,
          'icon-offset': [-15, -16.5],
          'icon-allow-overlap': true,
          'text-field': ['get', 'like_count'],
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-size': 12,
          'text-offset': [-0.75, -1.4],
          'text-anchor': 'top',
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#FFF',
        },
      });
    } catch (err) {
      // ignore
    }
  }

  if (!map.getLayer('building-comment-icons')) {
    try {
      map.addLayer({
        id: 'building-comment-icons',
        type: 'symbol',
        source: 'clicked-buildings',
        filter: [
          'any',
          ['all', ['>', ['get', 'commentsCount'], 0], ['!=', ['get', 'admin_comment_flag'], true], ['!=', ['get', 'type'], 'CONTRACTOR']],
          ['all', ['>', ['get', 'commentsCount'], 1], ['==', ['get', 'admin_comment_flag'], true], ['!=', ['get', 'type'], 'CONTRACTOR']],
        ],
        minzoom: 1,
        layout: {
          'icon-image': 'comment-icon',
          'icon-size': 0.8,
          'icon-offset': [15, -13.5],
          'icon-allow-overlap': true,
          'text-field': ['get', 'commentsCount'],
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-size': 12,
          'text-offset': [1, -1.6],
          'text-anchor': 'top',
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#202125',
        },
      });
    } catch (err) {
      // ignore
    }
  }

  if (!map.getLayer('building-jaakko-emojis')) {
    try {
      map.addLayer({
        id: 'building-jaakko-emojis',
        type: 'symbol',
        source: 'clicked-buildings',
        filter: ['all', ['==', ['get', 'admin_comment_flag'], true], ['!=', ['get', 'no_feed_comment_flag'], true], ['!=', ['get', 'type'], 'CONTRACTOR']],
        minzoom: 1,
        layout: {
          'icon-image': 'jaakko-icon',
          'icon-size': 0.4,
          'icon-offset': [0, 40],
          'icon-allow-overlap': true,
        },
      });
    } catch (err) {
      // ignore
    }
  }
};

export default BuildingMap;